import React, { useRef } from "react";

interface Props {
  editorRef: React.RefObject<HTMLDivElement>;
}

const HeadingPicker: React.FC<Props> = ({ editorRef }) => {
  const savedRangeRef = useRef<Range | null>(null);


  const applyHeading = (value: string) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    // Restore saved range if present
    if (savedRangeRef.current) {
      try {
        selection.removeAllRanges();
        selection.addRange(savedRangeRef.current);
      } catch (err) {
        // ignore
      }
    }

  const range = selection.getRangeAt(0);
  if (range.collapsed) return;

  // Clone the original range to remember the insertion position
  const originalRange = range.cloneRange();

  // Extract the selected contents
  const extracted = range.extractContents();

    // If user selected only whitespace/newlines, avoid creating empty heading
    const hasContent = Array.from(extracted.childNodes).some((n) => {
      if (n.nodeType === Node.TEXT_NODE) return n.textContent?.trim() !== "";
      return true;
    });
    if (!hasContent) {
      // nothing meaningful selected
      return;
    }

    // Create heading element
    const tag = value === "" ? "p" : value;
  const headingEl = document.createElement(tag);
  // Make the heading inline as requested
  headingEl.style.display = "inline";
    headingEl.style.fontSize = "25px";
  headingEl.appendChild(extracted);

    // Insert the heading at the original range start so it stays in place
    try {
      originalRange.collapse(true);
      originalRange.insertNode(headingEl);
    } catch (err) {
      // Fallback: insert at current range position
      range.insertNode(headingEl);
    }

    // Normalize/cleanup: if the inserted heading is inside a paragraph, move it out
    if (headingEl.parentElement && headingEl.parentElement !== editorRef.current) {
      const parent = headingEl.parentElement;
      if (parent && parent.tagName.toLowerCase() === "p") {
        parent.parentNode?.insertBefore(headingEl, parent.nextSibling);
        // If parent becomes empty, remove it
        if (parent.textContent?.trim() === "") parent.remove();
      }
    }

    // Place the caret after the inserted heading
    const newRange = document.createRange();
    newRange.setStartAfter(headingEl);
    newRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(newRange);

    editorRef.current.focus();
    savedRangeRef.current = null;
  };

  return (
    <div className="size-fit">
      <select
        defaultValue=""
        onChange={(e) => applyHeading(e.target.value)}
        onMouseDown={() => {
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            savedRangeRef.current = selection.getRangeAt(0).cloneRange();
          }
        }}
        className="rounded border px-2 py-1"
      >
        <option value="">Default</option>
        <option value="h1">H1</option>
        <option value="h2">H2</option>
        <option value="h3">H3</option>
      </select>
    </div>
  );
};

export default HeadingPicker;
